import { useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Alert from '@material-ui/lab/Alert';
import { useAsync } from 'react-use';
import { FieldValidation } from '@rjsf/utils';
import { AIOneTrustServiceApiRef, validateOneTrust } from '../../services/AIOneTrust.service';

export class NotApprovedError extends Error {
  constructor(appShortname: string) {
    super(`Application ${appShortname} has not completed OneTrust assessment`);
    this.name = 'NotApprovedError';
  }
}

export interface OneTrustValidationData {
  isApproved: boolean;
  appShortname: string;
}

interface AppShortnameData {
  shortName?: string;
}

type ScaffolderFormData = {
  cmdbAppShortName?: AppShortnameData;
  isCustomerFacing?: string;
  containsPII?: string;
} & Record<string, unknown>;

interface ScaffolderFormContext {
  formData?: ScaffolderFormData;
}

export const AppShortnameOneTrustValidator = ({
  onChange,
  formContext,
}: FieldExtensionComponentProps<OneTrustValidationData | null>) => {
  const oneTrustService = useApi(AIOneTrustServiceApiRef);
  const typedFormContext = formContext as ScaffolderFormContext | undefined;
  const cmdbAppShortName = typedFormContext?.formData?.cmdbAppShortName;
  const appShortname = cmdbAppShortName?.shortName || '';
  const isCustomerFacing = typedFormContext?.formData?.isCustomerFacing || '';
  const containsPII = typedFormContext?.formData?.containsPII || '';
  const isCustomerFacingYes = isCustomerFacing.trim().toLowerCase() === 'yes';
  const containsPIIYes = containsPII.trim().toLowerCase() === 'yes';
  const requiresOneTrust = isCustomerFacingYes || containsPIIYes;

  const { loading, error, value } = useAsync(async () => {
    if (!requiresOneTrust) {
      return undefined;
    }

    if (!appShortname) {
      return undefined;
    }

    const policy = await oneTrustService.fetchOneTrustPolicy();
    const isApproved = validateOneTrust(policy, appShortname);

    if (!isApproved) {
      throw new NotApprovedError(appShortname);
    }

    return {
      isApproved: true,
      appShortname,
    };
  }, [appShortname, requiresOneTrust, oneTrustService]);

  useEffect(() => {
    if (loading) {
      return;
    }

    let nextValue: OneTrustValidationData | null;
    if (!requiresOneTrust) {
      nextValue = { isApproved: true, appShortname: appShortname || 'N/A' };
    } else if (error || value === undefined) {
      nextValue = null;
    } else {
      nextValue = value;
    }

    onChange(nextValue);
  }, [loading, error, value, onChange, requiresOneTrust, appShortname]);

  if (!requiresOneTrust || !appShortname) {
    return null;
  }

  return (
    <Grid container spacing={2} style={{ marginTop: '16px', marginBottom: '16px' }}>
      <Grid item xs={12}>
        {loading && (
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <CircularProgress size={20} aria-label={`Loading OneTrust validation status for ${appShortname}`} />
            </Grid>
            <Grid item>
              <Typography>Checking if {appShortname} has completed OneTrust assessment...</Typography>
            </Grid>
          </Grid>
        )}

        {!loading && error && error instanceof NotApprovedError && (
          <Alert severity="error">
            OneTrust Assessment Required: {appShortname} has not completed the OneTrust assessment. Please complete the
            GenAI onboarding process first. Follow this documentation:{' '}
            <Link
              href="https://genai.aa.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
            >
              GenAI Onboarding Process
            </Link>
            . If you have any questions, reach out to{' '}
            <Link
              href="mailto:DL_GaaS@aa.com"
              style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
            >
              DL_GaaS@aa.com
            </Link>
            .
          </Alert>
        )}
        {!loading && error && !(error instanceof NotApprovedError) && (
          <Alert severity="error">
            Unable to Verify OneTrust Status: We encountered an issue while checking the OneTrust assessment status for{' '}
            {appShortname}. This may be due to a temporary service issue or invalid data. Please try again later, or
            contact{' '}
            <Link
              href="mailto:DL_GaaS@aa.com"
              style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
            >
              DL_GaaS@aa.com
            </Link>{' '}
            for assistance.
          </Alert>
        )}
      </Grid>
    </Grid>
  );
};

export const appShortnameOneTrustValidatorValidation = (
  value: OneTrustValidationData | null,
  validation: FieldValidation,
) => {
  if (!value || !value.isApproved) {
    validation.addError('Application must complete OneTrust assessment before proceeding');
  }
};
